import React, { useContext, useState } from 'react';
import classnames from 'classnames';
import ConfigurationContext from '../../../contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import MetsIcon from '../../../../commons/components/MetsIcon';
import Button from '../../../../commons/components/Button';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-responsive-modal';
import ChipList from '../../../../commons/components/ChipList';
import FilteredCheckList from 'commons/components/FilteredCheckList';
import { getPerkList } from 'UserRegistration/components/SubscriptionPackage';
import { synchronizeSelectedSubscriptionItems, getEnabledItemIds } from '../../../resources/utils/packageSelectionUtils';

function SynchronizedSubscriptionItemSelector(props) {

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const { packageSelectorData, setPackageSelectorData } = props;
    const { t } = useTranslation(componentConfig.i18nNamespaces);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [availableStates, setAvailableStates] = useState([{
        label: "",
        selected: false,
        disabled: false,
        itemData: "",
        value: null
    }]);
    const isDisabled = registrationData.registrationModel.subscription.selectedGroup.groupId == null;
    const chipListItems = getChipListItems();
    const className = classnames('synced-subscription-item-selector', { disabled: isDisabled }, props.className);
    const closeIcon = (<MetsIcon name="svg_close" />);
    const confirmButtonLabel = t('synchronizedSubscriptionItemSelector.confirm', { stateCount: getSelectedCountPlaceholder(availableStates.filter(item => item.selected && !item.disabled).length) });
    const disabledItemCount = availableStates.filter((item) => item.disabled).length;
    const disclaimerText = disabledItemCount ? t('synchronizedSubscriptionItemSelector.disclaimer') : "";
    const stateSelectorTitle = t('synchronizedSubscriptionItemSelector.title', { planName: registrationData.registrationModel.subscription.selectedGroup.description });
    
    /**
     * Open & Load the lightbox
     */
    function openSubscriptionItemSelectorModal() {

        updateAvailableStatesFromPackageSelectorData(packageSelectorData, registrationData);

        // open lightbox
        setIsModalOpen(true);

    }

    /**
     * Confirm the subscription item selection, close the modal and apply changes to the registrationModel.
     */
    function confirmSubscriptionItemSelection() {

        const selectedItemIds = availableStates
                                    .filter((item) => item.selected)
                                    .map((item) => {return item.value});
        const selectedItemAssociatedKeys = availableStates
                                    .filter((item) => item.selected)
                                    .map((item) => {return item.itemData});

        let registrationDataCopy = Object.assign({}, registrationData);
        let packageSelectorDataCopy = Object.assign({}, packageSelectorData);

        registrationDataCopy.selectedSubscriptionItems[registrationDataCopy.registrationModel.subscription.selectedGroup.groupKey] = selectedItemIds;
        registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = selectedItemIds;
        registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys = selectedItemAssociatedKeys;
        registrationDataCopy.invoiceItemIDs = getEnabledItemIds(selectedItemIds, packageSelectorData);
        
        registrationDataCopy = synchronizeSelectedSubscriptionItems(registrationDataCopy, packageSelectorData);

        // Update perk lists
        setPackageSelectorData(updatePerkLists(packageSelectorDataCopy, registrationDataCopy));
        
        // update our state
        setRegistrationData(registrationDataCopy);

        // close lightbox
        setIsModalOpen(false);
    }

    
    /**
     * Each time a state is selected (or unselected), synchronize that selection status with availableStates
     * @param {} e 
     */
     function onStateSelectorChange(e) {
        
        const availableStatesCopy = Object.assign([{}], availableStates);

        if (e && e.target.value) {
        
            const selectedStateId = parseInt(e.target.value);
            const clickedItem = availableStatesCopy.find(item => item.value == selectedStateId);
            const index = availableStatesCopy.indexOf(clickedItem);

            clickedItem.selected = e.target.checked;

            if (index !== -1) {
                availableStatesCopy[index] = clickedItem;
            }

        }

        setAvailableStates(availableStatesCopy);

    }


    /**
     * Update perk lists for the package tiles
     * @param {*} packageSelectorDataCopy 
     * @returns 
     */
    function updatePerkLists(packageSelectorDataCopy, registrationDataCopy) {
        
        // reset the status of all groups in case no states are selected
        packageSelectorDataCopy.availableSubscriptionItemGroups.forEach((group) => {
            group.enabled = true;
            group.perkList = getPerkList(group, registrationDataCopy, t); 
        });

        packageSelectorDataCopy.availableSubscriptionItemGroups.forEach((group) => {
            group.perkList = getPerkList(group, registrationDataCopy, t);
        });

        return packageSelectorDataCopy;

    }

    /**
     * 
     * @returns Get the list of data to populate the ChipList component
     */
    function getChipListItems() {

        if (!registrationData.registrationModel.subscription.selectedGroup.groupId) {
            return [];
        }

        return packageSelectorData.availableSubscriptionItemGroups
            .find((group) => {
                return group.id == registrationData.registrationModel.subscription.selectedGroup.groupId;
            })
            .subscriptionItems
                .filter((item) => { 
                    return registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.includes(item.id); 
                }).map((item) => {
                    return { 
                        disabled: !item.enabled,
                        label: item.description.concat(item.enabled ? "" : "*"),
                        value: item.id
                    };
                });        
    }

    /**
     * When no states/items are selected we return the "Add" label.
     * When there is at least 1 state/item selected we return the "Modify" label.
     * 
     * @returns Add/Modify button label
     */
    function renderAddModifyButtonLabel() {

        if (!registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds) {
            return t('synchronizedSubscriptionItemSelector.add');
        }

        return registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length > 0
            ? t('synchronizedSubscriptionItemSelector.modify')
            : t('synchronizedSubscriptionItemSelector.add');
    }


    /**
     * Remove a Subscription Item from the selected subscription item data
     * @param {} item 
     */
    function removeChipListItem(item) {
        
        let registrationDataCopy = Object.assign({}, registrationData);
        let packageSelectorDataCopy = Object.assign({}, packageSelectorData);
        const index = registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.indexOf(item.value);

        if (index > -1) {
            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.splice(index, 1);
            registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys.splice(index, 1);
        }

        // update state selector states data
        updateAvailableStatesFromPackageSelectorData(packageSelectorDataCopy, registrationDataCopy);

        // update list of perks in the group tiles
        packageSelectorDataCopy = updatePerkLists(packageSelectorDataCopy, registrationDataCopy);

        // synchronize across tiles
        registrationDataCopy = synchronizeSelectedSubscriptionItems(registrationDataCopy, packageSelectorDataCopy);

        registrationDataCopy.invoiceItemIDs = getEnabledItemIds(registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds, packageSelectorDataCopy);
        
        // update our state        
        setRegistrationData(registrationDataCopy);       
        setPackageSelectorData(packageSelectorDataCopy);        
    }


    /**
     * Build the availableStates data from PackageSelector and Registration data
     */
     function updateAvailableStatesFromPackageSelectorData(packageSelectorData, registrationData) {
        
        const localAvailableStates = packageSelectorData.availableSubscriptionItemGroups
            .find((group) => {return group.id == registrationData.registrationModel.subscription.selectedGroup.groupId})
                .subscriptionItems
                .map((item) => {return { 
                    label: getStateItemLabel(item), 
                    value: item.id, 
                    disabled: !item.enabled, 
                    itemData: item.associatedItemKey,
                    selected: registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.includes(item.id),
                    visible: true
                };})

        setAvailableStates(localAvailableStates);
        
        return localAvailableStates;
    }    
    
    /**
     * If count > 0, this method returns a string in the format "(N)"  
     * where N is selectedStateCount. Otherwise it returns an empty string.
     * @param {*} registrationModel 
     * @returns (string)
     */
    function getSelectedCountPlaceholder(count) {

        if (count == 0) {
            return "";
        }        
        
        return t('stateCount', { count: count});

    }

    /**
     * Gets the lable to display for a state checkbox (may include agencies count)
     */
    function getStateItemLabel(subscriptionItem) {

        let label = subscriptionItem.description;

        if (subscriptionItem.enabled && subscriptionItem.agenciesCount && subscriptionItem.agenciesCount > 0) {
            label = label + " (" + subscriptionItem.agenciesCount + ")"
        }

        if (!subscriptionItem.enabled) {
            label = label + "*";
        }

        return label;

    }

    /**
     * Render the disclaimer text to appear in the chip selector.
     * 
     * @param {*} chipListItems 
     * @returns 
     */
    const renderChipsDisclaimerText = () => {

        const disabledItemCount = chipListItems.filter((item) => item.disabled).length;
 
        if (disabledItemCount > 0) {
            return t('synchronizedSubscriptionItemSelector.disclaimer');
        }
        
        return "";
    }

    /**
     * Renders the temporary discount banner
     * METS-316: Temporary code until August 1st 2023.
     * @returns {JSX.Element}
     */
    const renderDiscountBanner = () => {

        const bannerText = t("synchronizedSubscriptionItemSelector.discountBanner");

        if (bannerText) {
            return <div className="discount-banner">{bannerText}</div>
        }

    };

    const renderEapSection = () => {
        const vasItems = componentConfig?.componentDefinition?.PublicPersonalInformation?.props?.availableVasItems;
    
        if (!vasItems || vasItems.length === 0) {
            return null;
        }
    
        return (
            <>
                {vasItems.map((vasItem) => {
                    return React.createElement(
                        componentConfig.vasList[vasItem.vasComponentName],
                        {
                            ...vasItem,
                            isSelectedByDefault: false,
                            vasItemId: vasItem.id,
                        }
                    );
                })}
            </>
        );
    };
    
    

    /**
     * 
     * @returns Discount box content
     */
    const renderDiscountBox = () => {

        const groupKey = registrationData.registrationModel.subscription.selectedGroup.groupKey;
        const frequency = registrationData.registrationModel.subscription.selectedFrequency;
        const isLabelDependentOnFrequency = t('synchronizedSubscriptionItemSelector.discountBox.'  + frequency + "." + groupKey + ".labels", "");
        const frequencySpecifier = isLabelDependentOnFrequency ? frequency + "." : "";

        const labels = t('synchronizedSubscriptionItemSelector.discountBox.'  + frequencySpecifier + groupKey + ".labels").split("|");
        const costs = t('synchronizedSubscriptionItemSelector.discountBox.' + frequency + "." + groupKey + ".costs").split("|");
        const lowerBounds = t('synchronizedSubscriptionItemSelector.discountBox.' + frequencySpecifier + groupKey + ".requirements.lowerBounds").split("|");
        const upperBounds = t('synchronizedSubscriptionItemSelector.discountBox.' + frequencySpecifier + groupKey + ".requirements.upperBounds").split("|");
        const selectedStatesCount = availableStates.filter((item) => { return item.selected && !item.disabled; }).length;

        if (labels.length > 1 && costs.length > 1 && lowerBounds.length > 1 && upperBounds.length > 1) {

            return (
                <div className="discounts-box">
                    <p className="title">{t('synchronizedSubscriptionItemSelector.discountBox.title')}</p>
                    <div className="discounts">
                        {labels.map((label, index) => {

                            return (
                                <div className={classnames("discount", { "current": selectedStatesCount >= parseInt(lowerBounds[index]) && selectedStatesCount <= upperBounds[index] })} key={index}>
                                    <div className="label">{label}</div>
                                    <div className="value">{costs[index]}</div>
                                </div>
                            );

                        })}
                    </div>
                </div>
            );

        }

    };

    return (
        <>
            <div className={className} data-testid="syncedSubscriptionItemSelector">

                {renderDiscountBanner()}

                <div className="flexbox">
                    <MetsIcon className="map-icon" name="svg_statesMap" />
                    <div>
                        <h4>{t('selectYourState', {stateCountPlaceholder: t('stateCount', { count: registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length})})}</h4>

                        <div className="chip-list-disclaimer-text">
                            {renderChipsDisclaimerText()}
                        </div>

                        {chipListItems.length > 0 && <ChipList
                            id="subscriptionChipList"
                            items={chipListItems}
                            onChipClick={(item) => { removeChipListItem(item); }}
                        />}

                        <Button 
                            disabled={isDisabled}
                            id="addModifySubscriptionItemButton"
                            label={renderAddModifyButtonLabel()}
                            onClick={openSubscriptionItemSelectorModal}
                        />

                        <Modal 
                            center 
                            classNames={{
                                modal: "synced-subscription-item-selector-modal"
                            }}
                            closeIcon={closeIcon}
                            modalId="syncedSubscriptionItemSelectorModal"
                            onClose={() => { setIsModalOpen(false); }} 
                            open={isModalOpen}>

                            <div className="modal-heading">
                                <MetsIcon className="map-icon" name="svg_statesMap" />
                                <h4>{stateSelectorTitle}</h4>
                            </div>

                            <div className="modal-content">

                                <FilteredCheckList
                                    selectorTitle={t('synchronizedSubscriptionItemSelector.list.title')}
                                    selectAllLabel={t('selectAll')}
                                    itemIdPrefix="subscriptionItem"
                                    items={availableStates}
                                    onCheckboxChange={(e) => {onStateSelectorChange(e)}}
                                    searchPlaceholder={t('synchronizedSubscriptionItemSelector.search.placeholder')}
                                />

                                {renderDiscountBox()}

                            </div>

                            <div className="modal-disclaimer-text">
                                {disclaimerText}
                            </div>
                            
                            <div className="react-responsive-modal-button-bar">
                                <a href="#" onClick={() => { setIsModalOpen(false); }}>{t('cancel')}</a>
                                <Button
                                    disabled={availableStates.filter((item) => item.selected).length === 0}
                                    id="syncedSubscriptionItemSelectorConfirmButton"
                                    label={confirmButtonLabel}
                                    onClick={() => { confirmSubscriptionItemSelection(); }}
                                />
                            </div>

                        </Modal>

                    </div>
                </div>

            </div>

            {renderEapSection()}

        </>
    );

}

SynchronizedSubscriptionItemSelector.propTypes = {
    className: PropTypes.string,
    packageSelectorData: PropTypes.object,
    setPackageSelectorData: PropTypes.func
};

SynchronizedSubscriptionItemSelector.defaultProps = {

};

export default SynchronizedSubscriptionItemSelector;
